import React, { useEffect } from "react";
import { Modal, Form, Input, Select, InputNumber, Switch } from "antd";
import {
  useGetAllStandAloneConfigurationsQuery,
  useGetFieldsStandAloneQuery,
  useGetMeasurementsStandAloneQuery,
  useGetTaskStandAloneByIdQuery,
  useUpdateTaskStandAloneMutation,
} from "shared/api";
import { ColorDot } from "./ColorDot";

interface Props {
  open: boolean;
  onClose: () => void;
  taskId: number | null;
}

export const EditTaskModalStandAlone: React.FC<Props> = ({
  open,
  onClose,
  taskId,
}) => {
  const [form] = Form.useForm();

  // Servers
  const { data: serversResp, isLoading: loadingServers } =
    useGetAllStandAloneConfigurationsQuery();
  const serverOptions =
    serversResp?.map((s) => ({
      label: s.name,
      value: s.id,
    })) ?? [];

  const serverId = Form.useWatch("influxdb_server", form);
  const numericServerId = serverId ? Number(serverId) : undefined;
  const measurement = Form.useWatch("measurement", form);
  const enabled = Form.useWatch("enabled", form);

  // Measurements
  const { data: measurementsResp, isLoading: loadingMeasurements } =
    useGetMeasurementsStandAloneQuery(numericServerId!, {
      skip: !numericServerId,
    });

  const measurementOptions =
    measurementsResp?.measurements.map((m: string) => ({
      label: m,
      value: m,
    })) ?? [];

  // Fields
  const { data: fieldsResp, isLoading: loadingFields } =
    useGetFieldsStandAloneQuery(
      { serverId: numericServerId!, measurement },
      { skip: !numericServerId || !measurement }
    );

  const fieldOptions =
    fieldsResp?.fields.map((f: string) => ({ label: f, value: f })) ?? [];

  // CRUD
  const [updateTask, { isLoading: updating }] =
    useUpdateTaskStandAloneMutation();

  // Fetch task by id
  const { data: taskData } = useGetTaskStandAloneByIdQuery(
    { id: taskId! },
    { skip: !taskId }
  );

  useEffect(() => {
    if (taskData) {
      form.setFieldsValue(taskData);
    }
  }, [taskData, form]);

  const handleFinish = async (values: any) => {
    const { min_hard, min_soft, target_value, max_soft, max_hard } = values;

    if (
      (min_hard !== undefined &&
        min_soft !== undefined &&
        !(min_hard < min_soft)) ||
      (min_soft !== undefined &&
        target_value !== undefined &&
        !(min_soft < target_value)) ||
      (target_value !== undefined &&
        max_soft !== undefined &&
        !(target_value < max_soft)) ||
      (max_soft !== undefined &&
        max_hard !== undefined &&
        !(max_soft < max_hard))
    ) {
      Modal.error({
        title: "Validation error",
        content:
          "Values must follow: min_hard < min_soft < target_value < max_soft < max_hard",
        okButtonProps: {
          style: {
            backgroundColor: "#ff4d4f",
            color: "#fff",
            fontWeight: "bold",
            fontSize: "15px",
            borderRadius: "6px",
            height: "38px",
          },
        },
      });
      return;
    }

    try {
      if (taskId) {
        await updateTask({ id: taskId, ...values }).unwrap();
        form.resetFields();
      }
      onClose();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Failed to update standalone task", e);
    }
  };

  const themeColor = JSON.parse(
    localStorage.getItem("color-theme") || '"#002F5F"'
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      width={720}
      destroyOnClose
      confirmLoading={updating}
      okButtonProps={{
        style: {
          backgroundColor: themeColor,
          color: "#fff",
          borderColor: themeColor,
        },
      }}
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <div className="flex items-center justify-between max-w-[95%] mb-4">
          <h2 className="font-bold">Edit Standalone Task</h2>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">
              {enabled ? "Enabled" : "Disabled"}
            </span>
            <Form.Item name="enabled" valuePropName="checked" noStyle>
              <Switch
                onChange={(checked) => form.setFieldValue("enabled", checked)}
                style={{
                  backgroundColor: enabled ? "limegreen" : "tomato",
                }}
              />
            </Form.Item>
          </div>
        </div>

        <Form.Item name="name" label="Name" rules={[{ required: true }]}>
          <Input placeholder="Enter task name" />
        </Form.Item>

        <Form.Item
          name="influxdb_server"
          label="Data source"
          rules={[{ required: true }]}
        >
          <Select
            loading={loadingServers}
            options={serverOptions}
            placeholder="Select server"
          />
        </Form.Item>

        <Form.Item
          name="measurement"
          label="Sensor hub"
          rules={[{ required: true }]}
        >
          <Select
            loading={loadingMeasurements}
            options={measurementOptions}
            placeholder="Select measurement"
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>

        <Form.Item name="field" label="Sensor" rules={[{ required: true }]}>
          <Select
            loading={loadingFields}
            options={fieldOptions}
            placeholder="Select field"
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>

        <Form.Item
          name="lookback_days"
          label="Trend days"
          rules={[{ required: true }]}
        >
          <InputNumber min={1} max={5} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item name="priority" label="Priority">
          <Select
            placeholder="Select priority"
            options={[
              { label: "1", value: 1 },
              { label: "2", value: 2 },
              { label: "3", value: 3 },
              { label: "4", value: 4 },
              { label: "5", value: 5 },
            ]}
          />
        </Form.Item>

        {/* Limits */}
        <div className="space-y-3 pl-2">
          <div className="flex items-center gap-3">
            <span className="w-[120px]">Min Hard:</span>
            <Form.Item name="min_hard" noStyle>
              <InputNumber />
            </Form.Item>
            <ColorDot color="red" />
          </div>

          <div className="flex items-center gap-3">
            <span className="w-[120px]">Min Soft:</span>
            <Form.Item name="min_soft" noStyle>
              <InputNumber />
            </Form.Item>
            <ColorDot color="yellow" />
          </div>

          <div className="flex items-center gap-3">
            <span className="w-[120px]">Target Value:</span>
            <Form.Item name="target_value" noStyle>
              <InputNumber />
            </Form.Item>
            <ColorDot color="green" />
          </div>

          <div className="flex items-center gap-3">
            <span className="w-[120px]">Max Soft:</span>
            <Form.Item name="max_soft" noStyle>
              <InputNumber />
            </Form.Item>
            <ColorDot color="yellow" />
          </div>

          <div className="flex items-center gap-3">
            <span className="w-[120px]">Max Hard:</span>
            <Form.Item name="max_hard" noStyle>
              <InputNumber />
            </Form.Item>
            <ColorDot color="red" />
          </div>
        </div>
      </Form>
    </Modal>
  );
};
